// client/components/widgets/WidgetShell.jsx
// ✅ Widget-Wrapper — gleiche Card-/Hover-Sprache wie Schnellzugriff (Dashboard)
import React, { Suspense, memo } from "react";
import { ChevronDown, ChevronUp, X, GripVertical } from "lucide-react";
import { tw, cn } from "../../utils/themeClasses.js";
import { getWidgetById } from "./registry.js";
import AppButton from "../ui/AppButton.jsx";

/**
 * WidgetShell - Inhalt in einer „Card“ wie die Schnellzugriff-Kacheln
 */
const WidgetShell = memo(function WidgetShell({
  widgetId,
  isMinimized = false,
  onMinimize,
  onRemove,
  railWidth = 260,
  compact = true,
  dragHandleProps = null, // dnd-kit listeners
  lockRemove = false,
}) {
  const widget = getWidgetById(widgetId);
  if (!widget) {
    return null;
  }
  const {
    name,
    icon: Icon,
    component: WidgetComponent
  } = widget;
  return <div className={cn(
      "rounded-2xl border shadow-sm overflow-hidden",
      "transition-all duration-200 motion-reduce:transition-none",
      "md:hover:-translate-y-0.5",
      "relative",
      tw.card(true),
      tw.cardHover(true),
      "group"
    )}>
      <div className="flex items-center gap-1.5 px-2 py-1.5">
        <div {...dragHandleProps} className={cn("flex-shrink-0 p-0.5 rounded cursor-grab active:cursor-grabbing", "transition-colors", tw.textMuted(true), "hover:bg-transparent hover:text-blue-200/90")} title="Ziehen zum Verschieben">
          <GripVertical size={12} strokeWidth={2} />
        </div>

        <AppButton type="button" variant="ghost" onClick={onMinimize} className={cn("flex-1 flex items-center gap-1.5 text-left min-w-0 !justify-start !px-0 !py-0 min-h-0", "!bg-transparent hover:!bg-transparent focus-visible:!bg-transparent active:!bg-transparent focus-visible:ring-1 focus-visible:ring-blue-400/35")}>
          {Icon && <Icon size={13} className={cn("flex-shrink-0 text-blue-400")} strokeWidth={2} />}
          <span className={cn("flex-1 text-[12px] sm:text-[13px] leading-tight font-semibold truncate", tw.textSecondary(true))}>
            {name}
          </span>
          {isMinimized ? <ChevronDown size={14} className={cn("shrink-0", tw.textMuted(true))} /> : <ChevronUp size={14} className={cn("shrink-0", tw.textMuted(true))} />}
        </AppButton>

        {!lockRemove ? (
          <AppButton type="button" variant="ghost" onClick={onRemove} className={cn("flex-shrink-0 p-0.5 rounded transition-colors !min-h-0 !px-0.5 !py-0.5", tw.textMuted(true), "!bg-transparent hover:!bg-transparent hover:text-red-400 focus-visible:!bg-transparent active:!bg-transparent")} title="Widget entfernen">
            <X size={12} strokeWidth={2} />
          </AppButton>
        ) : (
          <span className={cn("text-[9px] px-1 font-semibold uppercase tracking-wide", "text-amber-400/90")} title="Bleibt bis Academy abgeschlossen">
            Pflicht
          </span>
        )}
      </div>

      {!isMinimized && <div className={cn("px-2 pb-2 pt-0.5", "border-t", tw.divider(true))}>
          <Suspense fallback={<WidgetLoadingState />}>
            <WidgetComponent railWidth={railWidth} compact={compact} />
          </Suspense>
        </div>}
    </div>;
});
function WidgetLoadingState() {
  return <div className="space-y-1 animate-pulse py-1">
      <div className={cn("h-2.5 rounded", tw.skeleton(true), "w-3/4")} />
      <div className={cn("h-2.5 rounded", tw.skeleton(true), "w-1/2")} />
    </div>;
}
export default WidgetShell;
